import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetProject } from '@/entities/project';

import { getIsUserLeftProjectPage } from '@/shared/lib/location';
import { Project } from '@/shared/types/Project';

import { useUserMinutesUpdatingWhenDubbingStarted } from './useUserMinutesUpdatingWhenDubbingStarted';

const UPDATE_INTERVAL = 4000;

type UseProjectUpdatingProps = {
  finalStatuses: Project['status'][];
  project?: Project;
  onProjectUpdated: (updatingProject: Project) => Promise<void>;
};

// Хук даёт ручку для запуска интервала, по которому получаем проект, пока он не будет иметь некоторый статус
export const useProjectUpdating = ({ finalStatuses, project, onProjectUpdated }: UseProjectUpdatingProps) => {
  const { refetch: refetchProject } = useGetProject();

  const [isProjectUpdatingStarted, setIsProjectUpdatingStarted] = useState(false);

  const projectUpdatingRef = useRef<ReturnType<typeof setInterval>>();


  const finalStatusesRef = useRef(finalStatuses);
  const onProjectUpdatedRef = useRef(onProjectUpdated);
  const projectIdRef = useRef(project?.id);


  useEffect(() => {
    finalStatusesRef.current = finalStatuses;
    onProjectUpdatedRef.current = onProjectUpdated;
    projectIdRef.current = project?.id;
  });

  const { resetIsUserUpdatedWhenDubbingStarted, updateUserIfDubbingStatusChanged } =
    useUserMinutesUpdatingWhenDubbingStarted();

  const startProjectUpdating = useCallback(() => {
    resetIsUserUpdatedWhenDubbingStarted();
    setIsProjectUpdatingStarted(true);
  }, [resetIsUserUpdatedWhenDubbingStarted]);


  const fetchAndUpdateProject = useCallback(async () => {
    const currentProjectId = projectIdRef.current;
    if (!currentProjectId) {
      return;
    }

    const isUserLeftProject = getIsUserLeftProjectPage(currentProjectId);

    if (isUserLeftProject) {
      return;
    }

    const { data: updatingProject } = await refetchProject();

    if (updatingProject) {
      await updateUserIfDubbingStatusChanged(updatingProject);
    }

    return updatingProject;
  }, [refetchProject, updateUserIfDubbingStatusChanged]);

  useEffect(() => {
    if (isProjectUpdatingStarted && finalStatusesRef.current.length) {
      // isLoading нужен чтобы повторные запросы не уходили, пока предыдущий в статусе pending
      let isLoading = false;

      const intervalCallback = async () => {
        if (!isLoading) {
          isLoading = true;

          const updatingProject = await fetchAndUpdateProject();

          if (updatingProject && finalStatusesRef.current.includes(updatingProject.status)) {
            clearInterval(projectUpdatingRef?.current);

            await onProjectUpdatedRef.current(updatingProject);

            setIsProjectUpdatingStarted(false);
          }
          isLoading = false;
        }
      };

      projectUpdatingRef.current = setInterval(() => {
        void intervalCallback();
      }, UPDATE_INTERVAL);
    }

    return () => {
      clearInterval(projectUpdatingRef?.current);
    };
  }, [isProjectUpdatingStarted, fetchAndUpdateProject]);

  return {
    startProjectUpdating,
    fetchAndUpdateProject,
  };
};
